/**
 * @voltxampere/web — Bench grid generator (Sprint 12).
 *
 * Produces N×M resistor grids for the canvas perf walkthrough.
 * `bench-50` → 5 cols × 10 rows (50 components), `bench-500`
 * → 20 cols × 25 rows (500 components). Grids render through
 * the Sprint 12 renderer with a known component count so the
 * Şefim görsel-doğrulama pass can confirm 60 fps with DevTools'
 * Performance tab.
 *
 * The fixtures are NOT shipped through the schema — they exist
 * solely to feed the renderer in the dev server. Sprint 19+
 * persistence will round-trip them.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import type { Circuit, ComponentInstance, Wire } from "@voltxampere/schema";

// See `canvas-demo.ts` for the hardcoded-version rationale.
const SCHEMA_VERSION = "0.4.0";

const NOW = 1745539200000;
const SPACING = 80;

function generateGrid(cols: number, rows: number, name: string, id: string): Circuit {
  const components: ComponentInstance[] = [];
  const wires: Wire[] = [];
  const halfW = ((cols - 1) * SPACING) / 2;
  const halfH = ((rows - 1) * SPACING) / 2;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const x = c * SPACING - halfW;
      const y = r * SPACING - halfH;
      const rowKey = String(r);
      const colKey = String(c);
      components.push({
        id: `R${rowKey}-${colKey}`,
        type: "resistor",
        position: { x, y },
        rotation: 0,
        flipped: false,
        label: `R${String(r * cols + c)}`,
        params: { resistance: 1000 },
        terminals: [],
      });
      // Horizontal connector to the next column.
      if (c + 1 < cols) {
        wires.push({
          id: `w-${rowKey}-${colKey}-h`,
          netId: "net-bench",
          style: "direct",
          path: [
            { x: x + 20, y },
            { x: x + SPACING - 20, y },
          ],
        });
      }
    }
  }
  return {
    schemaVersion: SCHEMA_VERSION,
    id,
    name,
    createdAt: NOW,
    updatedAt: NOW,
    nodes: [],
    nets: [{ id: "net-bench", name: "bench", isGround: false }],
    components,
    wires,
    groups: [],
    annotations: [],
    assertions: [],
    simulationSettings: {
      defaultAnalysis: "dc",
      dc: {
        temperature: 27,
        gmin: 1e-12,
        reltol: 1e-3,
        abstol: 1e-12,
        maxIterations: 100,
        enableDeltaVDamping: true,
        toleranceStrategy: "strict",
        enableAnderson: false,
        enableArmijo: false,
      },
      transient: {
        tstart: 0,
        tstop: 1e-3,
        method: "trap",
        reltol: 1e-3,
        abstol: 1e-12,
      },
      ac: { start: 1, stop: 1e9, points: 100, sweep: "dec" },
    },
    metadata: {},
  };
}

export const bench50: Circuit = generateGrid(
  5,
  10,
  "Bench · 50 component grid",
  "bbbbbbbb-1111-1111-1111-111111111111",
);

export const bench500: Circuit = generateGrid(
  20,
  25,
  "Bench · 500 component grid",
  "bbbbbbbb-2222-2222-2222-222222222222",
);
